import React from 'react';

export interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
}

/**
 * Button Component v2 - FacturXPro Design System
 *
 * Bouton avec nouvelles couleurs Teal, hover effects et variants multiples
 * Basé sur le design Scandinave/Volvo minimaliste
 *
 * @example
 * ```tsx
 * // Bouton primaire avec icon
 * <Button variant="primary" icon={<PlusIcon />}>
 *   Nouveau devis
 * </Button>
 *
 * // Bouton danger avec loading
 * <Button variant="danger" loading>
 *   Supprimer
 * </Button>
 *
 * // Bouton ghost (transparent)
 * <Button variant="ghost" size="sm">
 *   Annuler
 * </Button>
 * ```
 *
 * Référence: DESIGN_SYSTEM.md section "Boutons"
 */
export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  loading = false,
  disabled = false,
  fullWidth = false,
  className = '',
  onClick,
  type = 'button',
}) => {
  // Variant classes
  const variantClasses = {
    primary:
      'bg-white text-teal-700 hover:shadow-2xl shadow-lg hover:bg-white/95 border-2 border-teal-100',
    secondary:
      'bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:from-teal-700 hover:to-teal-800 hover:shadow-2xl shadow-lg shadow-teal-500/30',
    ghost:
      'bg-transparent text-gray-700 hover:bg-gray-100 border border-gray-300',
    danger:
      'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 hover:shadow-2xl shadow-lg shadow-red-500/30',
    success:
      'bg-gradient-to-r from-teal-500 to-lime-500 text-white hover:from-teal-600 hover:to-lime-600 hover:shadow-2xl shadow-lg',
    outline:
      'bg-transparent text-teal-700 border-2 border-teal-600 hover:bg-teal-50 hover:shadow-lg',
  };

  // Size classes
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-8 py-3.5 text-base',
    lg: 'px-10 py-4 text-lg',
  };

  // Hover animation
  const hoverClasses =
    'hover:-translate-y-0.5 transition-all duration-300 active:translate-y-0';

  // Disabled state
  const disabledClasses = disabled || loading
    ? 'opacity-50 cursor-not-allowed hover:translate-y-0 hover:shadow-none'
    : '';

  // Full width
  const widthClasses = fullWidth ? 'w-full' : '';

  // Icon spacing
  const iconSpacing = icon ? 'flex items-center gap-2' : '';

  return (
    <button
      type={type}
      className={`
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${hoverClasses}
        ${disabledClasses}
        ${widthClasses}
        ${iconSpacing}
        rounded-2xl
        font-bold
        whitespace-nowrap
        ${className}
      `.trim()}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading && (
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current" />
      )}
      {!loading && icon && iconPosition === 'left' && (
        <span className="flex-shrink-0">{icon}</span>
      )}
      <span>{children}</span>
      {!loading && icon && iconPosition === 'right' && (
        <span className="flex-shrink-0">{icon}</span>
      )}
    </button>
  );
};

export default Button;
